/**
 * M27 Step 1 — Welcome (PR 2).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useUser } from "@clerk/clerk-react";
import {
  Brain,
  CheckCircle2,
  HelpCircle,
  Pause,
  Play,
  Sparkles,
  UserCheck,
  Zap,
} from "lucide-react";
import { OnboardingIntegrationCard } from "../../components/onboarding/OnboardingIntegrationCard";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { api } from "../../lib/api";
import { useOnboardingAutoSave } from "../../lib/use-onboarding-autosave";
import { StepLayout } from "./StepLayout";

type AutonomyMode = "conservative" | "standard" | "aggressive";

const _MODE_KEYS: Array<{
  key: AutonomyMode;
  labelKey: string;
  taglineKey: string;
  bodyKey: string;
  fitForKey: string;
  icon: typeof Pause;
}> = [
  {
    key: "conservative",
    labelKey: "app.s1_mode_conservative_label",
    taglineKey: "app.s1_mode_conservative_tagline",
    bodyKey: "app.s1_mode_conservative_body",
    fitForKey: "app.s1_mode_conservative_fit",
    icon: Pause,
  },
  {
    key: "standard",
    labelKey: "app.s1_mode_standard_label",
    taglineKey: "app.s1_mode_standard_tagline",
    bodyKey: "app.s1_mode_standard_body",
    fitForKey: "app.s1_mode_standard_fit",
    icon: Play,
  },
  {
    key: "aggressive",
    labelKey: "app.s1_mode_aggressive_label",
    taglineKey: "app.s1_mode_aggressive_tagline",
    bodyKey: "app.s1_mode_aggressive_body",
    fitForKey: "app.s1_mode_aggressive_fit",
    icon: Zap,
  },
];

export function Step1WelcomeReal() {
  const { t } = useTranslation();
  const { user } = useUser();
  const qc = useQueryClient();
  const auto = useOnboardingAutoSave("welcome");

  const status = useQuery({
    queryKey: ["ai-status"],
    queryFn: () => api.getAiStatus(),
  });

  const [mode, setMode] = useState<AutonomyMode>("conservative");
  useEffect(() => {
    if (status.data?.autonomy_mode) {
      setMode(status.data.autonomy_mode as AutonomyMode);
    }
  }, [status.data?.autonomy_mode]);

  const setAutonomy = useMutation({
    mutationFn: (next: AutonomyMode) => api.setAutonomyMode(next),
    onSuccess: (snapshot) => {
      qc.setQueryData(["ai-status"], snapshot);
    },
  });

  const firstName = useMemo(() => {
    const f = user?.firstName?.trim();
    return f && f.length > 0 ? f : t("app.s1_default_firstname");
  }, [user, t]);

  function pick(next: AutonomyMode) {
    setMode(next);
    setAutonomy.mutate(next);
    auto.save({ autonomy_mode: next });
  }

  return (
    <StepLayout
      slug="welcome"
      title={t("app.onb_step_welcome")}
      onContinue={async () => {
        await setAutonomy.mutateAsync(mode);
        auto.save({ autonomy_mode: mode, completed_via: "continue" });
      }}
      continueLabel={t("app.s1_continue_to_company")}
    >
      <div className="flex flex-col gap-7">
        <section
          className="relative overflow-hidden rounded-3xl border border-ai-subtle px-6 py-10 sm:px-10 sm:py-14"
          style={{
            background:
              "radial-gradient(circle at 0% 0%, rgba(0, 211, 194, 0.10), transparent 50%), radial-gradient(circle at 100% 100%, rgba(168, 70, 191, 0.10), transparent 50%), var(--surface-default)",
          }}
        >
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-blue">
            <SparkleMark />
            <span>{t("app.s1_brand_tagline")}</span>
          </div>
          <h1 className="mt-3 font-en-display text-3xl font-bold leading-tight text-brand-deep sm:text-4xl">
            {t("app.s1_hero_welcome", { name: firstName })}
          </h1>
          <p className="mt-4 max-w-2xl text-base text-text-secondary">
            {t("app.s1_hero_body")}
          </p>
        </section>

        <section className="rounded-2xl border border-ai-subtle bg-ai-bg p-5">
          <header className="flex items-center gap-2">
            <SparkleMark />
            <h2 className="text-base font-semibold text-text-primary">
              {t("app.s1_how_we_work")}
            </h2>
          </header>
          <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
            <CharacterColumn
              icon={Brain}
              title={t("app.s1_what_i_do")}
              items={[
                t("app.s1_what_i_do_1"),
                t("app.s1_what_i_do_2"),
                t("app.s1_what_i_do_3"),
                t("app.s1_what_i_do_4"),
                t("app.s1_what_i_do_5"),
              ]}
            />
            <CharacterColumn
              icon={UserCheck}
              title={t("app.s1_what_you_control")}
              items={[
                t("app.s1_what_you_control_1"),
                t("app.s1_what_you_control_2"),
                t("app.s1_what_you_control_3"),
                t("app.s1_what_you_control_4"),
                t("app.s1_what_you_control_5"),
              ]}
            />
          </div>
        </section>

        <OnboardingIntegrationCard
          integrationType="anthropic"
          titleKey="app.s1_anthropic_title"
          bodyKey="app.s1_anthropic_body"
          icon={Sparkles}
        />

        <section className="flex flex-col gap-3">
          <header>
            <h2 className="text-base font-semibold text-text-primary">
              {t("app.s1_pick_mode")}
            </h2>
            <p className="mt-1 text-sm text-text-secondary">
              {t("app.s1_change_anytime")}{" "}
              <Link
                to="/ai/behavior"
                className="text-brand hover:underline"
              >
                {t("app.s1_ai_behavior_link")}
              </Link>
              .
            </p>
          </header>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {_MODE_KEYS.map((m) => (
              <AutonomyCard
                key={m.key}
                mode={m.key}
                label={t(m.labelKey)}
                tagline={t(m.taglineKey)}
                body={t(m.bodyKey)}
                fitFor={t(m.fitForKey)}
                icon={m.icon}
                selected={mode === m.key}
                onPick={() => pick(m.key)}
              />
            ))}
          </div>
        </section>

        <section className="flex flex-wrap items-center justify-center gap-6 border-t border-border-subtle pt-4">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary"
            onClick={() => {
              auto.save({ tour_requested: true });
              alert(t("app.s1_tour_alert"));
            }}
          >
            <HelpCircle size={14} aria-hidden />
            {t("app.s1_tour_btn")}
          </button>
          <span className="text-text-tertiary" aria-hidden>
            ·
          </span>
          <a
            href="/docs/onboarding/ai-explainer.md"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-text-secondary hover:text-text-primary"
          >
            <Sparkles size={14} aria-hidden />
            {t("app.s1_what_ai_does")}
          </a>
        </section>
      </div>
    </StepLayout>
  );
}

function SparkleMark() {
  return (
    <span
      aria-hidden
      className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-brand-teal"
      style={{
        animation: "solvait-sparkle-pulse 1.2s ease-out 1",
      }}
    >
      <style>{`
        @keyframes solvait-sparkle-pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.15); filter: drop-shadow(0 0 6px var(--brand-teal, #00d3c2)); }
          100% { transform: scale(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="solvait-sparkle-pulse"] { animation: none !important; }
        }
      `}</style>
      <Sparkles size={14} aria-hidden />
    </span>
  );
}

function CharacterColumn({
  icon: Icon,
  title,
  items,
}: {
  icon: typeof Brain;
  title: string;
  items: string[];
}) {
  return (
    <div className="rounded-xl border border-border-subtle bg-surface-default p-4">
      <header className="flex items-center gap-2">
        <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-ai-bg text-ai">
          <Icon size={14} aria-hidden />
        </span>
        <h3 className="text-sm font-semibold text-text-primary">{title}</h3>
      </header>
      <ul className="mt-3 flex flex-col gap-2">
        {items.map((it) => (
          <li
            key={it}
            className="flex items-start gap-2 text-sm text-text-secondary"
          >
            <CheckCircle2
              size={14}
              aria-hidden
              className="mt-0.5 shrink-0 text-brand-teal"
            />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function AutonomyCard({
  mode,
  label,
  tagline,
  body,
  fitFor,
  icon: Icon,
  selected,
  onPick,
}: {
  mode: AutonomyMode;
  label: string;
  tagline: string;
  body: string;
  fitFor: string;
  icon: typeof Pause;
  selected: boolean;
  onPick: () => void;
}): ReactNode {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={selected}
      aria-label={t("app.s1_mode_aria", { label, tagline })}
      className={[
        "group relative flex flex-col gap-2 rounded-xl border p-4 text-start transition-shadow hover:shadow-sm",
        selected
          ? "border-brand bg-ai-bg ring-2 ring-brand/30"
          : "border-border-subtle bg-surface-default",
      ].join(" ")}
    >
      {selected && (
        <span
          aria-hidden
          className="absolute inset-x-0 top-0 h-0.5 rounded-t-xl bg-brand-gradient-h"
        />
      )}
      <header className="flex items-center gap-2">
        <span
          className={[
            "flex h-8 w-8 items-center justify-center rounded-lg",
            selected
              ? "bg-brand text-brand-fg"
              : "bg-surface-sunken text-text-secondary",
          ].join(" ")}
        >
          <Icon size={14} aria-hidden />
        </span>
        <h3 className="text-sm font-semibold text-text-primary">{label}</h3>
        {selected && (
          <CheckCircle2
            size={14}
            aria-hidden
            className="ms-auto text-brand"
          />
        )}
      </header>
      <p className="text-sm font-medium text-text-primary">{tagline}</p>
      <p className="text-xs text-text-secondary">{body}</p>
      <span className="mt-auto inline-flex w-fit rounded-full bg-surface-sunken px-2 py-0.5 text-xs text-text-tertiary">
        {fitFor}
      </span>
      <input
        type="radio"
        name="autonomy_mode"
        value={mode}
        checked={selected}
        readOnly
        className="sr-only"
      />
    </button>
  );
}
